import { useEffect, useState } from "react";
import { NavigateOptions, URLSearchParamsInit, useLocation } from "react-router-dom";
import { ProductDetail } from "../../utils/interfaces";

type Props = {
  searchParams: URLSearchParams;
  setSearchParams: (
    nextInit?:
      | URLSearchParamsInit
      | ((prev: URLSearchParams) => URLSearchParamsInit),
    navigateOpts?: NavigateOptions
  ) => void;
  dataProductDetail: ProductDetail;
};

// Function to set the correct state after a page refresh based on search parameters
function setCorrectStateAfterPageRefresh(searchParams: URLSearchParams, setCurrentStep, setSelectedProductIndex, setSelectedBtns, setDisableBtns, setSelectedSubcategoryIndex, setSelectedCategoryIndex, setSelectedMacrocategoryIndex) {
  const macro = searchParams.get("macro");
  const cat = searchParams.get("cat");
  const sub = searchParams.get("sub");
  const prod = searchParams.get("prod");

  if (prod) {
    setCurrentStep(""); // Last step, no next step
    setSelectedProductIndex(prod);
    setSelectedBtns({ macro: true, cat: true, sub: true, prod: true });
    setDisableBtns({ cat: false, sub: false, prod: false });
  } else if (sub) {
    setCurrentStep("Prodotto");
    setSelectedSubcategoryIndex(sub);
    setSelectedBtns({ macro: true, cat: true, sub: true, prod: false });
    setDisableBtns({ cat: false, sub: false, prod: false });
  } else if (cat) {
    setCurrentStep("Sottocategoria");
    setSelectedCategoryIndex(cat);
    setSelectedBtns({ macro: true, cat: true, sub: false, prod: false });
    setDisableBtns({ cat: false, sub: false, prod: true });
  } else if (macro) {
    setCurrentStep("Categoria");
    setSelectedMacrocategoryIndex(macro);
    setSelectedBtns({ macro: true, cat: false, sub: false, prod: false });
    setDisableBtns({ cat: false, sub: true, prod: true });
  } else {
    setCurrentStep("Macrocategoria");
    setSelectedBtns({ macro: false, cat: false, sub: false, prod: false });
    setDisableBtns({ cat: true, sub: true, prod: true });
  }
}

function useCatalogFn({ searchParams, setSearchParams, dataProductDetail }: Props) {
  const [selectedMacrocategoryIndex, setSelectedMacrocategoryIndex] = useState<string>();
  const [selectedProductIndex, setSelectedProductIndex] = useState<string>();
  const [selectedSubcategoryIndex, setSelectedSubcategoryIndex] = useState<string>();
  const [selectedCategoryIndex, setSelectedCategoryIndex] = useState<string>();
  const [currentStep, setCurrentStep] = useState("");
  const [currentSelected, setCurrentSelected] = useState({
    macro: "",
    cat: "",
    sub: "",
    prod: "",
  });
  const [disableBtns, setDisableBtns] = useState({
    cat: true,
    sub: true,
    prod: true,
  });
  const [selectedBtns, setSelectedBtns] = useState({
    macro: false,
    cat: false,
    sub: false,
    prod: false,
  });

  // Set the correct state after a page refresh based on search parameters
  useEffect(() => {
    setCorrectStateAfterPageRefresh(searchParams, setCurrentStep, setSelectedProductIndex, setSelectedBtns, setDisableBtns, setSelectedSubcategoryIndex, setSelectedCategoryIndex, setSelectedMacrocategoryIndex);
  }, [searchParams]);

  const location = useLocation();

  // Reset states when navigating from header
  useEffect(() => {
    if (location.state?.from === "header") {
      setSelectedBtns({
        macro: false,
        cat: false,
        sub: false,
        prod: false,
      });
      setSelectedMacrocategoryIndex(null);
      setSelectedCategoryIndex(null);
      setSelectedSubcategoryIndex(null);
      setSelectedProductIndex(null);
      setDisableBtns({
        cat: true,
        sub: true,
        prod: true,
      });
      setCurrentSelected({
        macro: null,
        cat: null,
        sub: null,
        prod: null,
      });
      setSearchParams({});
      setCurrentStep("Macrocategoria");
    }
  }, [location.state?.from, setSearchParams]);

  function handleMacroBtn(e: any, macro: any) {
    e.preventDefault();
    setSelectedMacrocategoryIndex(e.target.value);
    setSelectedBtns((prev) => ({ ...prev, macro: true }));
    setDisableBtns((prev) => ({
      ...prev,
      cat: false,
    }));
    setCurrentSelected((prev) => ({
      ...prev,
      macro: null,
      cat: null,
      sub: null,
      prod: null,
    }));

    setSearchParams({ macro: macro.idNodo });
    setCurrentStep("Categoria");
  }

  function handleCatBtn(e, cat) {
    e.preventDefault();
    setSelectedCategoryIndex(e.target.value);
    setSelectedBtns((prev) => ({ ...prev, cat: true }));
    setDisableBtns((prev) => ({
      ...prev,
      sub: false,
    }));
    setCurrentSelected((prev) => ({
      ...prev,
      cat: null,
      sub: null,
      prod: null,
    }));

    setSearchParams({ cat: cat.idNodo });
    setCurrentStep("Sottocategoria");
  }

  async function handleSubBtn(e, cat) {
    e.preventDefault();
    setSelectedSubcategoryIndex(e.target.value);
    setSelectedBtns((prev) => ({ ...prev, sub: true }));
    setDisableBtns((prev) => ({
      ...prev,
      prod: false,
    }));
    setCurrentSelected((prev) => ({
      ...prev,
      sub: null,
      prod: null,
    }));

    setSearchParams({ sub: cat.idNodo });
    setCurrentStep("Prodotto");
  }

  async function handleProdBtn(e, cat) {
    setSelectedProductIndex(e.target.value);
    setSelectedBtns((prev) => ({ ...prev, prod: true }));
    setCurrentSelected((prev) => ({
      ...prev,
      prod: null,
    }));

    setSearchParams({ prod: cat.idNodo });
    setCurrentStep("");
  }

  function unselectMacro() {
    console.log("unselect outside");
    if (selectedBtns.macro) {
      console.log("unselect inside");
      setSelectedBtns((prev) => ({
        ...prev,
        macro: false,
        cat: false,
        sub: false,
        prod: false,
      }));
      setSelectedMacrocategoryIndex(null);
      setSelectedCategoryIndex(null);
      setSelectedSubcategoryIndex(null);
      setSelectedProductIndex(null);
      setDisableBtns((prev) => ({
        ...prev,
        cat: true,
        sub: true,
        prod: true,
      }));
    }
    setSearchParams({});
    setCurrentStep("Macrocategoria");
  }

  function unselectCat() {
    if (selectedBtns.cat) {
      setSelectedBtns((prev) => ({
        ...prev,
        cat: false,
        sub: false,
        prod: false,
      }));
      setSelectedCategoryIndex(null);
      setSelectedSubcategoryIndex(null);
      setSelectedProductIndex(null);
      setDisableBtns((prev) => ({
        ...prev,
        sub: true,
        prod: true,
      }));
    }
    setSearchParams({ macro: selectedMacrocategoryIndex });
    setCurrentStep("Categoria");
  }

  function unselectSub() {
    if (selectedBtns.sub) {
      setSelectedBtns((prev) => ({ ...prev, sub: false, prod: false }));
      setSelectedSubcategoryIndex(null);
      setSelectedProductIndex(null);
      setDisableBtns((prev) => ({
        ...prev,
        prod: true,
      }));
    }
    setSearchParams({ cat: selectedCategoryIndex });
    setCurrentStep("Sottocategoria");
  }

  function unselectProd() {
    if (selectedBtns.prod) {
      setSelectedBtns((prev) => ({ ...prev, prod: false }));
      setSelectedProductIndex(null);

      setSearchParams({ sub: dataProductDetail.sottocategoriaId });
      setCurrentStep("Prodotto");
    }
  }

  return {
    handleMacroBtn,
    handleCatBtn,
    handleSubBtn,
    handleProdBtn,
    selectedMacrocategoryIndex,
    selectedProductIndex,
    selectedSubcategoryIndex,
    selectedCategoryIndex,
    currentSelected,
    disableBtns,
    selectedBtns,
    setSelectedMacrocategoryIndex,
    setSelectedCategoryIndex,
    setSelectedSubcategoryIndex,
    setSelectedProductIndex,
    setDisableBtns,
    setSelectedBtns,
    unselectMacro,
    unselectCat,
    unselectSub,
    unselectProd,
    setCurrentSelected,
    currentStep,
  };
}

export default useCatalogFn;
